/**
 * Bindings callable pour la gestion des crédits côté client.
 *
 * - `ensureBillingDoc()` — bootstrap idempotent du doc
 *   `/users/{uid}/billing/current` (octroi du crédit gratuit 7 jours à
 *   l'inscription). À appeler depuis `Dashboard.tsx` ou `AppShell.tsx` dès
 *   que `useAuth()` a chargé l'utilisateur.
 * - `consumeCreditForFiche()` — gate à déclencher **avant** `runPipeline`
 *   dans le flow de création de fiche. Retour `{ ok: false }` ⇒ afficher
 *   la modale d'upsell et bloquer la génération. Sur succès, renvoie un
 *   `refundToken` à passer à `refundCreditForFiche` si le pipeline échoue.
 * - `refundCreditForFiche(token)` — recrédite la source consommée quand
 *   le pipeline échoue en aval du consume. Le caller DOIT se prémunir
 *   contre les doubles-appels (ref guard) — le serveur est stateless.
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

export type CreditSource = "free" | "topup" | "plan";

export type ConsumeResponse =
  | {
      ok: true;
      remainingAfter: number;
      source: CreditSource;
      refundToken: string;
    }
  | {
      ok: false;
      code: "no-credits" | "subscription-inactive";
      message: string;
    };

export type RefundResponse =
  | { ok: true; creditedTo: CreditSource }
  | {
      ok: false;
      code: "invalid-token" | "billing-missing" | "network";
      message: string;
    };

const _ensureBillingDoc = httpsCallable<
  Record<string, never>,
  { created: boolean }
>(functions, "ensureBillingDoc");

const _consumeCredit = httpsCallable<Record<string, never>, ConsumeResponse>(
  functions,
  "consumeCreditForFiche",
);

const _refundCredit = httpsCallable<
  { refundToken: string },
  RefundResponse
>(functions, "refundCreditForFiche");

export async function ensureBillingDoc(): Promise<{ created: boolean }> {
  try {
    const res = await _ensureBillingDoc({});
    return res.data;
  } catch {
    // Le doc peut être écrit par un autre onglet entre-temps — on considère
    // ça comme un no-op. Ne jamais bloquer l'UI sur cet appel.
    return { created: false };
  }
}

export async function consumeCreditForFiche(): Promise<ConsumeResponse> {
  try {
    const res = await _consumeCredit({});
    return res.data;
  } catch (e) {
    const err = e as { code?: string; message?: string };
    return {
      ok: false,
      code: "no-credits",
      message: err.message ?? "Impossible de vérifier vos crédits.",
    };
  }
}

export async function refundCreditForFiche(
  refundToken: string,
): Promise<RefundResponse> {
  try {
    const res = await _refundCredit({ refundToken });
    return res.data;
  } catch (e) {
    const err = e as { code?: string; message?: string };
    return {
      ok: false,
      code: "network",
      message: err.message ?? "Impossible de recréditer le compte.",
    };
  }
}
